"use client";

import {
  createContext,
  use,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import type { Id } from "@convex/_generated/dataModel";
import type { StatementEmailKind } from "@convex/schemas/validators/billingDnc.lib";
import { escapeHtml } from "@/lib/documents/html";

export type ReplyMode = "reply" | "reply-all" | "forward";

export interface ComposeEmailDraft {
  toEmail?: string;
  toEmails?: Array<string>;
  toName?: string;
  contactId?: Id<"contacts">;
  leadId?: Id<"leads">;
  companyId?: Id<"companies">;
  orderId?: Id<"orders">;
  quoteId?: Id<"quotes">;
  quoteNumber?: string;
  subject?: string;
  bodyHtml?: string;
  ccEmails?: Array<string>;
  bccEmails?: Array<string>;
  templateId?: Id<"emailTemplates">;
  signatureId?: Id<"emailSignatures">;
  variables?: Record<string, string>;
  replyMode?: ReplyMode;
  replyToEmailId?: Id<"crmEmails">;
  inReplyToHeader?: string;
  referencesHeader?: string;
  threadId?: string;
  quotedHtml?: string;
  attachments?: Array<{
    storageId: Id<"_storage">;
    filename: string;
    mimeType: string;
    size: number;
  }>;
  draftId?: Id<"emailDrafts">;
  fromConnectionId?: Id<"gmailConnections">;
  preferredFromAddress?: string;
  restrictToSharedMailboxes?: boolean;
  statement?: { accountKey: string; kind: StatementEmailKind };
  bulkLeadIds?: Array<Id<"leads">>;
}

type ComposeState = "hidden" | "minimized" | "expanded";

export interface ReplyParams {
  contactId?: Id<"contacts">;
  leadId?: Id<"leads">;
  companyId?: Id<"companies">;
  orderId?: Id<"orders">;
  quoteId?: Id<"quotes">;
  toEmail: string;
  toName?: string;
  additionalToEmails?: Array<string>;
  ccEmails?: Array<string>;
  subject: string;
  bodyHtml?: string;
  bodyPlain?: string;
  crmEmailId?: Id<"crmEmails">;
  messageIdHeader?: string;
  referencesHeader?: string;
  threadId?: string;
  mode?: ReplyMode;
  inline?: boolean;
  inlineKey?: string;
  preferredFromAddress?: string;
}

interface ComposeEmailContextValue {
  draft: ComposeEmailDraft | null;
  state: ComposeState;
  inline: boolean;
  inlineKey: string | null;
  openCompose: (draft?: ComposeEmailDraft) => void;
  openReply: (params: ReplyParams) => void;
  closeCompose: () => void;
  minimizeCompose: () => void;
  restoreCompose: () => void;
}

const ComposeEmailContext = createContext<ComposeEmailContextValue | null>(
  null,
);

function buildReplyDraft(params: ReplyParams): ComposeEmailDraft {
  const quotedBody =
    params.bodyHtml ??
    (params.bodyPlain
      ? escapeHtml(params.bodyPlain).replaceAll("\n", "<br>")
      : undefined);

  return {
    contactId: params.contactId,
    leadId: params.leadId,
    companyId: params.companyId,
    orderId: params.orderId,
    quoteId: params.quoteId,
    toEmail: params.toEmail,
    toEmails:
      params.additionalToEmails && params.additionalToEmails.length > 0
        ? [params.toEmail, ...params.additionalToEmails]
        : undefined,
    toName: params.toName,
    ccEmails: params.ccEmails,
    subject: params.subject.startsWith("Re:")
      ? params.subject
      : `Re: ${params.subject}`,
    replyMode: params.mode ?? "reply",
    replyToEmailId: params.crmEmailId,
    inReplyToHeader: params.messageIdHeader,
    referencesHeader: params.referencesHeader,
    threadId: params.threadId,
    quotedHtml: quotedBody
      ? `<blockquote style="margin: 16px 0; padding: 8px 12px; border-left: 3px solid #d1d5db; color: #6b7280;">${quotedBody}</blockquote>`
      : undefined,
    preferredFromAddress: params.preferredFromAddress,
  };
}

function useComposeEmailState(): ComposeEmailContextValue {
  const [draft, setDraft] = useState<ComposeEmailDraft | null>(null);
  const [state, setState] = useState<ComposeState>("hidden");
  const [inline, setInline] = useState(false);
  const [inlineKey, setInlineKey] = useState<string | null>(null);

  const openCompose = useCallback((newDraft?: ComposeEmailDraft) => {
    setDraft(newDraft ?? {});
    setState("expanded");
    setInline(false);
    setInlineKey(null);
  }, []);

  const openReply = useCallback((params: ReplyParams) => {
    setDraft(buildReplyDraft(params));
    setState("expanded");
    setInline(!!params.inline);
    setInlineKey(params.inline ? (params.inlineKey ?? null) : null);
  }, []);

  const closeCompose = useCallback(() => {
    setDraft(null);
    setState("hidden");
    setInline(false);
    setInlineKey(null);
  }, []);

  const minimizeCompose = useCallback(() => {
    setState("minimized");
  }, []);

  const restoreCompose = useCallback(() => {
    setState("expanded");
  }, []);

  return {
    draft,
    state,
    inline,
    inlineKey,
    openCompose,
    openReply,
    closeCompose,
    minimizeCompose,
    restoreCompose,
  };
}

export function ComposeEmailProvider({ children }: { children: ReactNode }) {
  const value = useComposeEmailState();

  return (
    <ComposeEmailContext value={value}>
      {children}
    </ComposeEmailContext>
  );
}

export function useOptionalComposeEmail(): ComposeEmailContextValue | null {
  return use(ComposeEmailContext);
}

export function useComposeEmail(): ComposeEmailContextValue {
  const context = use(ComposeEmailContext);
  if (!context) {
    throw new Error(
      "useComposeEmail must be used within a ComposeEmailProvider",
    );
  }
  return context;
}
